import "./base.css"

import { FieldValues, UseControllerProps, useController } from "react-hook-form"
import React, { ReactNode } from "react"

import TextareaStyles from "./textarea.module.css"
import WrapWithError from "./wrap-with-error"
import classNames from "classnames"
import { useRequiredMark } from "./required-mark"
import { useTranslation } from "react-i18next"

type TextAreaProps = {
    transform?: {
        output: (value: string) => string
    }
    length?: number
    showLengthInfo?: boolean
    label?: ReactNode
    className?: string
    placeholder?: string
    cols?: HTMLTextAreaElement["cols"]
    rows?: HTMLTextAreaElement["rows"]
    wrap?: HTMLTextAreaElement["wrap"]
    required?: boolean
}

const Textarea = <T extends FieldValues>({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
    length,
    showLengthInfo,
    transform,
    placeholder,
    label,
    cols,
    rows,
    wrap,
    className,
    required,
}: TextAreaProps & UseControllerProps<T>) => {
    const {
        field: { onChange, onBlur, value, ref, ...rest },
        fieldState: { error, isTouched },
    } = useController({
        name,
        control,
        rules,
        defaultValue,
        shouldUnregister,
    })

    const { t } = useTranslation("shared")

    const showRequiredMark = useRequiredMark()

    const info =
        showLengthInfo && length && rows
            ? {
                  message:
                      t("remaining_chars", {
                          count:
                              length -
                              value
                                  .split("\n")
                                  .map((v: string, i: number) => (i < value.split("\n").length - 1 ? 45 : v.length))
                                  .reduce((a: number, b: number) => a + b, 0),
                      }) +
                      "; " +
                      t("remaining_lines", { count: rows - value.split("\n").length }),
              }
            : length
              ? {
                    message: t("remaining_chars", { count: length - value.length }),
                }
              : rows
                ? {
                      message: t("remaining_lines", { count: rows - value.split("\n").length }),
                  }
                : undefined

    return (
        <div
            className={classNames(TextareaStyles.block, {
                error: error,
                wasInFocus: (isTouched && value) || value || placeholder,
                // @ts-ignore
                [className]: className,
            })}
        >
            <WrapWithError error={error} info={info} id={name}>
                <textarea
                    id={name}
                    value={value}
                    onBlur={onBlur}
                    onChange={e => onChange(transform ? transform.output(e.target.value) : e)}
                    className="input"
                    ref={ref}
                    maxLength={length ? length + (rows ? rows - 1 : 0) : undefined}
                    cols={cols}
                    rows={rows}
                    wrap={wrap}
                    placeholder={placeholder}
                    aria-invalid={!!error}
                    aria-errormessage={"err-" + name}
                    required={required}
                    {...rest}
                />
                {label && (
                    <label className="label" htmlFor={name}>
                        {label}
                        {required && showRequiredMark && <span className="required"> *</span>}
                    </label>
                )}
            </WrapWithError>
        </div>
    )
}

export default Textarea
